import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '@/context/AppContext';
import { useNavigate } from 'react-router-dom';
import { formatUZS } from '@/lib/format';
import POSLayout from '@/components/layout/POSLayout';
import { Minus, Plus, Trash2, CreditCard, Banknote, Wallet, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PaymentMethod } from '@/types';

export default function POSCart() {
  const { t } = useTranslation();
  const { cart, updateQuantity, removeFromCart, setItemUnit, cartTotal, clearCart } = useApp();
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');

  const handleCheckout = () => {
    navigate('/pos/checkout', { state: { paymentMethod } });
  };

  const hasMurabaha = cart.some(c => c.product.is_murabaha_eligible);

  if (cart.length === 0) {
    return (
      <POSLayout>
        <div className="flex flex-col items-center justify-center h-[60vh] text-muted-foreground animate-fade-in">
          <p className="text-lg mb-2">{t('pos_cart.empty')}</p>
          <Button variant="outline" onClick={() => navigate('/pos')}>{t('pos_cart.add_products')}</Button>
        </div>
      </POSLayout>
    );
  }

  return (
    <POSLayout>
      <div className="flex flex-col lg:flex-row gap-4 p-4 animate-fade-in">
        <div className="flex-1 space-y-2">
          {cart.map(item => (
            <div key={item.product.id} className="flex items-center gap-4 bg-card border border-border rounded-xl p-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{item.product.name}</p>
                <p className="text-xs text-muted-foreground">
                  {formatUZS(item.unit_price)} × {item.quantity} {item.selected_unit || item.product.unit}
                </p>
              </div>
              {(item.product.allowed_units?.length ?? 0) > 1 && (
                <select
                  value={item.selected_unit || item.product.unit}
                  onChange={e => setItemUnit(item.product.id, e.target.value)}
                  className="text-xs bg-background border border-border rounded-md px-2 py-1.5 text-foreground max-w-[140px]"
                >
                  {item.product.allowed_units!.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              )}
              <div className="flex items-center gap-2">
                <button onClick={() => updateQuantity(item.product.id, item.quantity - 1)} className="w-7 h-7 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-accent">
                  <Minus size={12} />
                </button>
                <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                <button onClick={() => updateQuantity(item.product.id, item.quantity + 1)} className="w-7 h-7 rounded-full border border-border flex items-center justify-center text-muted-foreground hover:bg-accent">
                  <Plus size={12} />
                </button>
              </div>
              <span className="text-sm font-semibold text-foreground w-28 text-right">{formatUZS(item.total)}</span>
              <button onClick={() => removeFromCart(item.product.id)} className="text-destructive hover:bg-destructive/10 p-1.5 rounded-lg">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>

        <div className="w-full lg:w-80 space-y-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-sm text-muted-foreground mb-3">{t('pos_cart.payment_method')}</p>
            <div className="space-y-2">
              {([
                { method: 'cash' as PaymentMethod, icon: Banknote, label: t('pos_pay.cash') },
                { method: 'card' as PaymentMethod, icon: CreditCard, label: t('pos_pay.card') },
                { method: 'hisob_wallet' as PaymentMethod, icon: Wallet, label: t('pos_pay.wallet') },
                { method: 'murabaha' as PaymentMethod, icon: FileText, label: t('pos_pay.murabaha') },
              ]).map(pm => (
                <button
                  key={pm.method}
                  onClick={() => setPaymentMethod(pm.method)}
                  className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    paymentMethod === pm.method ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground hover:bg-accent'
                  }`}
                >
                  <pm.icon size={16} /> {pm.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">{t('pos_cart.total')}</span>
              <span className="font-semibold text-foreground">{formatUZS(cartTotal)}</span>
            </div>
            <Button onClick={handleCheckout} className="w-full mt-3">
              {t('pos_cart.pay')}
            </Button>
            {hasMurabaha && (
              <Button variant="outline" onClick={() => navigate('/pos/murabaha')} className="w-full mt-2">
                {t('pos_pay.nasiya')}
              </Button>
            )}
          </div>

          <button onClick={clearCart} className="text-xs text-destructive hover:underline w-full text-center">
            {t('pos_cart.clear')}
          </button>
        </div>
      </div>
    </POSLayout>
  );
}
